import React from 'react'
import PropTypes from 'prop-types'

import { v4 as uuidv4, v5 as uuidv5 } from 'uuid'

// core components
import GridContainer from '../../components/Grid/GridContainer.js'
import GridItem from '../../components/Grid/GridItem.js'

import { AgGridReact } from 'ag-grid-react'

// material-ui components
import LinearProgress from '@material-ui/core/LinearProgress'
import Button from '@material-ui/core/Button'
import Snackbar from '@material-ui/core/Snackbar'
import Alert from '@material-ui/lab/Alert'
import ButtonGroup from '@material-ui/core/ButtonGroup'
import CircularProgress from '@material-ui/core/CircularProgress'

// Icons
import SaveIcon from '@material-ui/icons/Save'
import AddCircleIcon from '@material-ui/icons/AddCircle'
import EditIcon from '@material-ui/icons/Edit'
import LabelIcon from '@material-ui/icons/Label'
import shopmateLogo from '../../assets/img/colorImages/ShopMateLogo.svg'

// components
import CheckboxRenderer from '../../components/AtlasGrid/CheckboxRenderer.js'
import AtlasButtonBar from '../../components/AtlasButtonBar/AtlasButtonBar.js'

// Helper to generate the headings
import { productHeadings } from './Headings/ProductHeadings.js'

// report components
import { getSiteProducts, getSiteProductLookups, saveProductOverrides, addToShelfEdgeLabels } from '../../layouts/Aws.js'
import PriceFileNotImportedError from './PriceFileNotImportedError.js'
import UnsavedChangesPrompt from '../../components/UnsavedChangesPrompt/UnsavedChangesPrompt.js'
import BulkEditor from './BulkEditor.js'
import NewProductDialog from './NewProductDialog.js'
import ConfirmSaveDialog from './ConfirmSaveDialog.js'

const PRODUCT_NAMESPACE = 'b0fa6371-8ed7-4635-94a8-dde29b3edec2'

export default function SiteProducts ({ siteId, categoryFilter = null }) {
  const [isLoading, setLoading] = React.useState(false)
  const [siteLoaded, setSiteLoaded] = React.useState(false)
  const [gridHeadings, setGridHeadings] = React.useState([])
  const [gridData, setGridData] = React.useState(null)
  const [productUpdates, setProductUpdates] = React.useState({}) // we store as an object so that multiple edits to the same row get stored as a single object (keyed on product_id)
  const [selectedProducts, setSelectedProducts] = React.useState([])
  const [initialCategoryFilter, setInitialCategoryFilter] = React.useState(null)
  const [firstRenderFinished, setFirstRenderFinished] = React.useState(false)
  const [snackbarOpen, setSnackbarOpen] = React.useState(false)
  const [snackbarMessage, setSnackbarMessage] = React.useState('')
  const [snackbarSeverity, setSnackbarSeverity] = React.useState('error')
  const [bulkEditorOpen, setBulkEditorOpen] = React.useState(false)
  const [newProductOpen, setNewProductOpen] = React.useState(false)
  const [siteCategories, setSiteCategories] = React.useState([])
  const [ageRestrictions, setAgeRestrictions] = React.useState([])
  const [taxRates, setTaxRates] = React.useState([])
  const [wholesalerFiles, setWholesalerFiles] = React.useState([])
  const [overrideTypes, setOverrideTypes] = React.useState([])
  const [userCreatedProductDescriptions, setUserCreatedProductDescriptions] = React.useState([])
  const [isGridEditing, setGridEditing] = React.useState(false)
  const [editingColumnId, setEditingColumnId] = React.useState(null)
  const [confirmSaveDialogOpen, setConfirmSaveDialogOpen] = React.useState(false)
  const [isSaving, setSaving] = React.useState(false)

  const gridRef = React.useRef(null) // get reference to the grid object so we can use the api

  const defaultGridData = []

  const defaultColDef = {
    resizable: true,
    width: 120,
    sortable: true,
    floatingFilter: true,
    filterParams: {
      buttons: ['reset']
    }
  }

  // define a custom renderer for checkboxes that can be used
  const components = {
    checkboxRenderer: CheckboxRenderer
  }

  React.useEffect(() => {
    const loadSiteProducts = async () => {
      if (siteId !== null) {
        setSiteLoaded(false)
        setLoading(true)

        const siteProductLookups = await getSiteProductLookups(siteId)

        setSiteCategories(siteProductLookups.siteCategories)
        setAgeRestrictions(siteProductLookups.ageRestrictions)
        setTaxRates(siteProductLookups.taxRates)
        setWholesalerFiles(siteProductLookups.wholesalerFiles)
        setOverrideTypes(siteProductLookups.overrideTypes)

        setGridHeadings(productHeadings(false, siteProductLookups.taxRates, siteProductLookups.siteCategories, siteProductLookups.wholesalerFiles, siteProductLookups.overrideTypes, siteProductLookups.types, siteProductLookups.ageRestrictions))

        let siteProducts

        try {
          siteProducts = await getSiteProducts(siteId)

          const userCreatedProductDescriptions = []
          for (const product of siteProducts) {
            if (product.user_created.value) {
              userCreatedProductDescriptions.push(product.product_description.value)
            }
          }

          setUserCreatedProductDescriptions(userCreatedProductDescriptions)

          setSiteLoaded(true)
          setGridData(siteProducts)
          setLoading(false)
        } catch (err) {
          setLoading(false)
          if (err instanceof PriceFileNotImportedError) {
            setSnackbarMessage('No products available for site - probably due to Price File not yet being imported')
            setSnackbarSeverity('warning')
            setSnackbarOpen(true)
          } else {
            throw (err)
          }
        }
      }
    }

    loadSiteProducts()
  }, [siteId])

  const loadStagger = (api, index, timeout) => {
    setTimeout(() => {
      if (index < gridData.length) {
        const newItems = gridData.slice(index, index + 100000)

        api.applyTransaction({
          add: newItems,
          addIndex: undefined
        })

        loadStagger(api, index + 100000, 5000)
      } else {
        setGridData(null)
      }
    }, timeout)
  }

  // Grid events
  const onGridReady = (params) => {
    loadColumnState(params.api)
    loadColumnGroupState(params.api)

    console.log('Setting grid data...')

    loadStagger(params.api, 0, 1)

    console.log('Set grid data...')
  }

  // I had to chain the onFirstDataRendered and the useEffect[firstRenderFinished] code together because we only want the category filter to be
  // applied the very first time we load the page, and after that (if we choose another shop or something) we want it to be cleared.
  const onFirstDataRendered = (params) => {
    setInitialCategoryFilter(categoryFilter)

    setFirstRenderFinished(true)
  }

  React.useEffect(() => {
    if (initialCategoryFilter !== null) {
      const filter = {
        category_id: {
          type: 'set',
          values: initialCategoryFilter
        }
      }

      gridRef.current.api.setFilterModel(filter)
      gridRef.current.api.onFilterChanged()

      setInitialCategoryFilter(null) // once we've set the filter on intial load, we can clear it, so it doesn't apply again if we choose another site
    }
  }, [firstRenderFinished])

  const onCellValueChanged = (event) => {
    console.log('onCellValueChanged event:', event)

    updateProductsUpdatedList([event.data])
  }

  const updateProductsUpdatedList = (rowDataList) => {
    const newProductUpdates = { ...productUpdates } // create shallow copy of the product updates

    for (const row of rowDataList) {
      newProductUpdates[row.product_id.value] = row
    }

    setProductUpdates(newProductUpdates)
  }

  const onCellEditingStarted = (event) => {
    // we record that we are in an editing state, and which column is being edited - this is so we can properly deal with a right click context menu for the cell
    console.log('onCellEditingStarted:', event)

    setEditingColumnId(event.column.colId)

    setGridEditing(true)
  }

  const onCellEditingStopped = (event) => {
    console.log('onCellEditingStopped:', event)

    setEditingColumnId(null)

    setGridEditing(false)

    // without this, if the cell was already in a non validated state, it stays in that state
    if (event.oldValue === event.newValue) {
      event.data[event.colDef.field].lastValidation = true
    }
  }

  const onSelectionChanged = (event) => {
    setSelectedProducts(event.api.getSelectedRows())
  }

  // custom context menu for reverting overridden product attributes
  const getContextMenuItems = (params) => {
    console.log('getContextMenuItems clicked - params:', params)

    const menuItems = []

    const columnSelected = params.column.colDef.field
    const overrideSetTooltipString = 'Click to set the value back to the original RDP supplied value'
    const revertToRdpControlledString = 'Revert to RDP controlled value'

    // is THIS cell is being currently edited, DON'T show the custom context menu, as it will be referring to the pre-edit values which gets confusing
    // Instead, just show the browser context menu, that will let you copy and paste properly into the editable field
    if (isGridEditing && columnSelected === editingColumnId) {
      return
    }

    let displayString = 'Field already set to RDP controlled value'
    let tooltipString = ''
    let revertDisabled = true
    let addCustomMenuOption = true

    let menuAction = () => {}

    // price column is a bit of a special case as we need to revert two columns that will set the price back to 0 PenceOverRRP
    if (params.column.colDef.headerName === 'Price') {
      if (params.node.data.user_created.value) {
        addCustomMenuOption = false // if this is a user created product, we must leave the price as set amount and not allow it to be reverted
      } else {
        let priceOverridden = true

        if (params.node.data.markup_calculation_type.value === 'PriceOverRRP' &&
            (params.node.data.markup_calculation_value.value === null || Number(params.node.data.markup_calculation_value.value) === 0)) {
          priceOverridden = false
        }

        if (priceOverridden) {
          displayString = `${revertToRdpControlledString} - 0 Pence Over RRP`
          tooltipString = overrideSetTooltipString
          revertDisabled = false
        }

        menuAction = () => {
          params.node.setDataValue('markup_calculation_value', 0)
          params.node.setDataValue('markup_calculation_type', 'PriceOverRRP')
        }
      }
    } else if (params.column.colDef.editable && 'original' in params.node.data[columnSelected]) {
      // if the value is actually overridden
      if (params.node.data[columnSelected].original !== params.value) {
        // if the refData attribute is present, we know we have a drop down, so the display value needs to be looked up by its id
        if (params.column.colDef.refData) {
          displayString = `${revertToRdpControlledString} - ${params.column.colDef.refData[params.node.data[columnSelected].original]}`
        } else {
          displayString = `${revertToRdpControlledString} - ${params.node.data[columnSelected].original}`
        }

        tooltipString = overrideSetTooltipString
        revertDisabled = false
      }

      menuAction = () => {
        params.node.setDataValue(columnSelected, params.node.data[columnSelected].original)
      }
    } else {
      addCustomMenuOption = false
    }

    if (addCustomMenuOption) {
      menuItems.push({
        name: displayString,
        disabled: revertDisabled,
        action: menuAction,
        icon: `<img style="height: 24px" src="${shopmateLogo}">`,
        tooltip: tooltipString
      })

      menuItems.push('separator')
    }

    return (menuItems.concat(params.defaultItems))
  }

  // deal with saving the state of the grid between loads
  const onColumnVisible = (event) => {
    saveColumnState(event.columnApi.getColumnState())
  }

  const onColumnPinned = (event) => {
    saveColumnState(event.columnApi.getColumnState())
  }

  const onColumnResized = (event) => {
    saveColumnState(event.columnApi.getColumnState())
  }

  const onColumnMoved = (event) => {
    saveColumnState(event.columnApi.getColumnState())
  }

  const onColumnGroupOpened = (event) => {
    saveColumnGroupState(event.columnApi.getColumnGroupState())
  }

  const saveColumnState = (columnState) => {
    localStorage.setItem('atlas_product_grid_column_state', JSON.stringify(columnState))
  }

  const saveColumnGroupState = (columnGroupState) => {
    localStorage.setItem('atlas_product_grid_column_group_state', JSON.stringify(columnGroupState))
  }

  const loadColumnState = (api) => {
    const columnState = JSON.parse(localStorage.getItem('atlas_product_grid_column_state'))

    if (columnState !== null) {
      // NOTE - setColumnState is deprecated in favour of applyColumnState, but applyColumnState seems to currently be bugged and NOT WORK - PJF 25/1/2023
      api.columnModel.columnApi.setColumnState(columnState)
    }
  }

  const loadColumnGroupState = (api) => {
    const columnGroupState = JSON.parse(localStorage.getItem('atlas_product_grid_column_group_state'))

    if (columnGroupState !== null) {
      api.columnModel.columnApi.setColumnGroupState(columnGroupState)
    }
  }

  const handleSnackbarClose = (event, reason) => {
    setSnackbarOpen(false)
  }

  // bulk editor functions
  const onBulkEditClick = (event) => {
    setBulkEditorOpen(true)
  }

  const onBulkEditorClose = (event) => {
    setBulkEditorOpen(false)
  }

  const onBulkEditorApply = (newValues) => {
    const newSelectedProducts = gridRef.current.api.getSelectedRows() // we don't want to modify the existing list of selectedProducts so we get a new copy of them

    for (const row of newSelectedProducts) {
      if (newValues.taxId !== 0) {
        // if we have selected revert back to RDP AND the row has an original value (user_created products won't)
        if (newValues.taxId === -1 && row.tax_id.original) {
          row.tax_id.value = row.tax_id.original
        } else {
          row.tax_id.value = newValues.taxId
        }
      }

      if (newValues.wholesalerFileId !== 0) {
        // if we have selected revert back to RDP AND the row has an original value (user_created products won't)
        if (newValues.wholesalerFileId === -1 && row.wholesaler_id.original) {
          row.wholesaler_id.value = row.wholesaler_id.original
        } else {
          row.wholesaler_id.value = newValues.wholesalerFileId
        }
      }

      if (newValues.categoryId !== 0) {
        // if we have selected revert back to RDP AND the row has an original value (user_created products won't)
        if (newValues.categoryId === -1 && row.category_id.original) {
          row.category_id.value = row.category_id.original
        } else {
          row.category_id.value = newValues.categoryId
        }
      }

      // we don't update markup values on user created products
      if (newValues.markupCalculationType !== 0 && !row.user_created.value) {
        // if we have selected revert back to RDP AND the row has an original value (user_created products won't)
        if (newValues.markupCalculationType === -1) {
          row.markup_calculation_value.value = 0
          row.markup_calculation_type.value = 'PriceOverRRP'
        } else {
          if (newValues.markupValue) {
            row.markup_calculation_value.value = newValues.markupValue
            row.markup_calculation_type.value = newValues.markupCalculationType
          }
        }
      }

      if (newValues.restrictedAgeId !== 0) {
        if (newValues.restrictedAgeId === -1 && row.restricted_age_id.original) {
          row.restricted_age_id.value = row.restricted_age_id.original
        } else {
          row.restricted_age_id.value = newValues.restrictedAgeId
        }
      }
    }

    const res = gridRef.current.api.applyTransaction({ update: newSelectedProducts })

    console.log('update res:', res)

    updateProductsUpdatedList(newSelectedProducts)
    setSelectedProducts(newSelectedProducts)

    setBulkEditorOpen(false)
  }

  const onAddToShelfEdgeLabelListClick = async () => {
    const shelfEdgeProducts = []

    for (const product of selectedProducts) {
      shelfEdgeProducts.push({
        product_id: product.product_id.value,
        quantity: 1
      })
    }

    try {
      await addToShelfEdgeLabels(siteId, shelfEdgeProducts)

      setSnackbarSeverity('success')
      setSnackbarMessage(`Added ${shelfEdgeProducts.length} products to Shelf Edge Label list`)
      setSnackbarOpen(true)
    } catch (err) {
      setSnackbarSeverity('error')
      setSnackbarMessage('Error - Failed to add products to Shelf Edge Label list')
      setSnackbarOpen(true)
      console.log('Error adding products to SEL list:', err)
    }
  }

  const onNewProductClick = () => {
    setNewProductOpen(true)
  }

  const onNewProductClose = () => {
    setNewProductOpen(false)
  }

  const onNewProductAdd = (newProductValues) => {
    // use normal description if short description not supplied (but only first 50 chars)
    const shortDescription = newProductValues.shortDescription.length > 0 ? newProductValues.shortDescription : newProductValues.description.substr(0, 50)

    console.log('new product values:', newProductValues)

    const res = gridRef.current.api.applyTransaction({
      add: [
        {
          barcode: {
            value: newProductValues.barcode
          },
          product_id: {
            value: newProductValues.barcode === null ? uuidv4() : uuidv5(newProductValues.barcode, PRODUCT_NAMESPACE)
          },
          product_num: {
            value: newProductValues.productCode,
            original: newProductValues.productCode
          },
          product_description: {
            value: newProductValues.description,
            original: newProductValues.description
          },
          product_short_description: {
            value: shortDescription,
            original: shortDescription
          },
          item_size_desc: {
            value: newProductValues.itemSizeDescription,
            original: newProductValues.itemSizeDescription
          },
          category_id: {
            value: newProductValues.categoryId,
            original: newProductValues.categoryId
          },
          tax_id: {
            value: newProductValues.taxId,
            original: newProductValues.taxId
          },
          wholesaler_id: {
            value: newProductValues.wholesalerFileId,
            original: newProductValues.wholesalerFileId
          },
          pack_cost: {
            value: newProductValues.packCost,
            original: newProductValues.packCost
          },
          pack_size: {
            value: newProductValues.packSize,
            original: newProductValues.packSize
          },
          unit_rrp: {
            value: newProductValues.unitRrp,
            original: newProductValues.unitRrp
          },
          price_marked: {
            value: newProductValues.priceMarked,
            original: newProductValues.priceMarked
          },
          markup_calculation_value: {
            value: newProductValues.markupValue
          },
          markup_calculation_type: {
            value: newProductValues.markupCalculationType
          },
          user_created: {
            value: true
          },
          type: {
            value: newProductValues.type
          },
          restricted_age_id: {
            value: newProductValues.restrictedAgeId,
            original: newProductValues.restrictedAgeId
          }
        }
      ]
    })

    console.log('update res:', res)

    const newRows = []

    // should only be one row
    for (const newRow of res.add) {
      newRows.push(newRow.data)
    }

    updateProductsUpdatedList(newRows)

    setNewProductOpen(false)

    setTimeout(function () {
      // scroll to the new product, and flash to show it
      gridRef.current.api.ensureNodeVisible(res.add[0], 'middle')

      gridRef.current.api.flashCells({ rowNodes: res.add })
    }, 250) // if I don't add the timeout, the comparator data doesn't yet seem to include the newly added product
  }

  const onCheckBarcodeExists = (barcode) => {
    // check to see if the product (barcode) already exists in our unfiltered data set
    let exists = false

    // unfortunately there doesn't seem to be a function access the data directly, where we could loop through until we found something and then exit -
    // we have to loop through everything, even after we've matched our barcode. It seems to perform fine with circa 50000 rows though.
    gridRef.current.api.forEachNode((node) => {
      if (node.data.barcode.value === barcode) {
        exists = true
      }
    })

    return (exists)
  }

  const handleSaveClick = () => {
    setConfirmSaveDialogOpen(true)
  }

  const saveProductUpdates = async (event) => {
    gridRef.current.api.showLoadingOverlay()

    setSaving(true)

    const productUpdatesArray = Object.values(productUpdates) // we turn the updates into an array here as we don't need them to be keyed by product_id for saving

    for (const update of productUpdatesArray) {
      if (update.restricted_age_id.value === -1) {
        update.restricted_age_id.value = null
      }

      if (update.priceValidation) {
        delete update.priceValidation
      }
    }

    console.log('product updates:', productUpdatesArray)

    try {
      await saveProductOverrides(siteId, productUpdatesArray)

      setProductUpdates({})
      setSaving(false)
    } catch (err) {
      setSnackbarSeverity('error')

      if (err.lastProcessedIndex > 0) {
        // some updates were saved, but not all - warn user, but remove the successful ones from the productUpdates object
        const newProductUpdates = { ...productUpdates } // create shallow copy of the product updates

        const successfulProductUpdates = productUpdatesArray.slice(0, err.lastProcessedIndex)

        for (const productUpdate of successfulProductUpdates) {
          delete newProductUpdates[productUpdate.product_id.value]
        }

        setProductUpdates(newProductUpdates)

        setSnackbarMessage(`Error - Only the first ${err.lastProcessedIndex} changes were saved; please click Save to try the remaining changes again, or contact Technical Support if the issue persists`)
      } else {
        setSnackbarMessage('Error - Failed to save price changes; click Save to try again, or contact Technical Support if the issue persists')
      }

      setSnackbarOpen(true)
      setSaving(false)

      console.log('Error - failed to save overrides:', JSON.stringify(err))
    }

    gridRef.current.api.hideOverlay()
  }

  const onConfirmSaveDialogClose = (event) => {
    setConfirmSaveDialogOpen(false)
  }

  // when save is pressed in the save dialog - this will be auto triggered if the dontShowConfirmSaveDialog value is true
  const handleConfirmSaveDialogSave = () => {
    setConfirmSaveDialogOpen(false)
    saveProductUpdates()
  }

  return (
    <>
      <UnsavedChangesPrompt when={Object.keys(productUpdates).length > 0} title="Unsaved Changes" />

      {
        isLoading
          ? <LinearProgress />
          : siteLoaded &&
          <GridContainer>
            <GridItem xs={12}>
              <AtlasButtonBar>
                <Button variant="contained" color="primary" onClick={handleSaveClick} disabled={Object.keys(productUpdates).length === 0}>
                  {
                    isSaving === true
                      ? <><CircularProgress color="inherit" size={20} />&nbsp;&nbsp;</>
                      : <><SaveIcon />&nbsp;</>
                  }
                  Save Changes { Object.keys(productUpdates).length > 0 && '(' + Object.keys(productUpdates).length + ')' }
                </Button>

                &nbsp;&nbsp;

                <ButtonGroup variant="text">
                  <Button color="primary" onClick={onNewProductClick}><AddCircleIcon />&nbsp;Add Product</Button>
                  <Button color="primary" disabled={!(selectedProducts.length > 1)} onClick={onBulkEditClick}><EditIcon />&nbsp;Bulk Edit { selectedProducts.length > 1 && '(' + selectedProducts.length + ')' }</Button>
                  <Button color="primary" disabled={!(selectedProducts.length > 0)} onClick={onAddToShelfEdgeLabelListClick}><LabelIcon />&nbsp;Add to Shelf Edge Label List { selectedProducts.length > 0 && '(' + selectedProducts.length + ')' }</Button>
                </ButtonGroup>
              </AtlasButtonBar>
            </GridItem>

            <GridItem xs={12}>
              <div className="ag-theme-alpine" style={{ width: '100%', height: '700px' }}>
                <AgGridReact
                  ref={gridRef}
                  rowData={defaultGridData}
                  defaultColDef={defaultColDef}
                  columnDefs={gridHeadings}
                  onGridReady={onGridReady}
                  onCellValueChanged={onCellValueChanged}
                  onCellEditingStarted={onCellEditingStarted}
                  onCellEditingStopped={onCellEditingStopped}
                  rowSelection={'multiple'}
                  suppressRowClickSelection={true}
                  components={components}
                  sideBar={{
                    toolPanels: [
                      {
                        id: 'columns',
                        labelDefault: 'Columns',
                        labelKey: 'columns',
                        iconKey: 'columns',
                        toolPanel: 'agColumnsToolPanel',
                        toolPanelParams: {
                          suppressRowGroups: true,
                          suppressValues: true,
                          suppressPivots: true,
                          suppressPivotMode: true,
                          suppressColumnFilter: true,
                          suppressColumnSelectAll: false,
                          suppressColumnExpandAll: false
                        }
                      },
                      {
                        id: 'filters',
                        labelDefault: 'Filters',
                        labelKey: 'filters',
                        iconKey: 'filter',
                        toolPanel: 'agFiltersToolPanel'
                      }
                    ],
                    position: 'left',
                    hiddenByDefault: false
                  }}
                  statusBar={{
                    statusPanels: [
                      { statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left' },
                      { statusPanel: 'agTotalRowCountComponent', align: 'center' },
                      { statusPanel: 'agFilteredRowCountComponent' },
                      { statusPanel: 'agSelectedRowCountComponent' },
                      { statusPanel: 'agAggregationComponent' }
                    ]
                  }}
                  overlayLoadingTemplate={'<span class="ag-overlay-loading-center" style="font-size: 18px">Saving data...</span>'}
                  getContextMenuItems={getContextMenuItems}
                  animateRows={true}
                  onColumnVisible={onColumnVisible}
                  onColumnPinned={onColumnPinned}
                  onColumnResized={onColumnResized}
                  onColumnMoved={onColumnMoved}
                  onColumnGroupOpened={onColumnGroupOpened}
                  onSelectionChanged={onSelectionChanged}
                  onFirstDataRendered={onFirstDataRendered}
                  tooltipShowDelay={0}
                />
              </div>
            </GridItem>
          </GridContainer>
      }

      <Snackbar open={snackbarOpen} autoHideDuration={5000} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} onClose={handleSnackbarClose}>
        <Alert severity={snackbarSeverity} variant="filled">{snackbarMessage}</Alert>
      </Snackbar>

      <BulkEditor
        open={bulkEditorOpen}
        onClose={onBulkEditorClose}
        taxRates={taxRates}
        overrideTypes={overrideTypes}
        wholesalerFiles={wholesalerFiles}
        siteCategories={siteCategories}
        ageRestrictions={ageRestrictions}
        selectedProductCount={selectedProducts.length}
        onApply={onBulkEditorApply}
      />

      <NewProductDialog
        open={newProductOpen}
        onClose={onNewProductClose}
        onProductAdd={onNewProductAdd}
        onCheckBarcodeExists={onCheckBarcodeExists}
        taxRates={taxRates}
        siteCategories={siteCategories}
        ageRestrictions={ageRestrictions}
        wholesalerFiles={wholesalerFiles}
        productDescriptions={userCreatedProductDescriptions}
      />

      <ConfirmSaveDialog
        open={confirmSaveDialogOpen}
        onSave={handleConfirmSaveDialogSave}
        onClose={onConfirmSaveDialogClose}
        isSaving={isSaving}
        totalChanges={Object.keys(productUpdates).length}
      />
    </>
  )
}

SiteProducts.propTypes = {
  siteId: PropTypes.string.isRequired,
  categoryFilter: PropTypes.array
}
